import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';

import { AdminProtectedRoute } from './components/AdminProtectedRoute';
import { CookieConsent } from './components/CookieConsent';
import { ProtectedRoute } from './components/ProtectedRoute';
import { initAnalyticsFromStoredConsent } from './lib/analytics';
import { useAuthStore } from './lib/auth';
import { syncLocale } from './lib/i18n';
import { applyTheme } from './lib/theme';
import { ToastProvider } from './lib/toast';
import { trpc, trpcClient } from './lib/trpc';
import EmailConfigPage from './pages/admin/EmailConfigPage';
import MetricsPage from './pages/admin/MetricsPage';
import StripeConfigPage from './pages/admin/StripeConfigPage';
import UsersPage from './pages/admin/UsersPage';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { AdminPage } from './pages/AdminPage';
import { CalendarPage } from './pages/CalendarPage';
import { CategoriesPage } from './pages/CategoriesPage';
import { DashboardPage } from './pages/DashboardPage';
import { DeveloperPage } from './pages/DeveloperPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import LandingPage from './pages/LandingPage';
import { Layout } from './pages/Layout';
import { LoginPage } from './pages/LoginPage';
import { PlanPage } from './pages/PlanPage';
import { ProvidersPage } from './pages/ProvidersPage';
import { RegisterPage } from './pages/RegisterPage';
import { ReportsPage } from './pages/ReportsPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SettingsPage } from './pages/SettingsPage';
import { SubscriptionsPage } from './pages/SubscriptionsPage';
import { TeamPage } from './pages/TeamPage';

const queryClient = new QueryClient();

// Sincroniza el tema guardado en el perfil con el documento
function ThemeSync() {
  const theme = useAuthStore(s => s.user?.theme);
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  return null;
}

// Sincroniza el idioma del perfil (user.locale) con i18n (M7, F8)
function LocaleSync() {
  const locale = useAuthStore(s => s.user?.locale);
  useEffect(() => {
    if (locale) syncLocale(locale);
  }, [locale]);
  return null;
}

function App() {
  useEffect(() => {
    initAnalyticsFromStoredConsent();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <ToastProvider>
          <CookieConsent />
          <ThemeSync />
          <LocaleSync />
          <Router>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DashboardPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscriptions"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SubscriptionsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/providers"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ProvidersPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/categories"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CategoriesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CalendarPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReportsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SettingsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/plan"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PlanPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TeamPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/developer"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DeveloperPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              {/* Rutas de Administración */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route
                path="/admin"
                element={
                  <AdminProtectedRoute>
                    <AdminPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminProtectedRoute>
                    <UsersPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/metrics"
                element={
                  <AdminProtectedRoute>
                    <MetricsPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/stripe"
                element={
                  <AdminProtectedRoute>
                    <StripeConfigPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/email"
                element={
                  <AdminProtectedRoute>
                    <EmailConfigPage />
                  </AdminProtectedRoute>
                }
              />

              {/* Ruta catch-all al final */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </ToastProvider>
      </trpc.Provider>
    </QueryClientProvider>
  );
}

export default App;
